import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Loader, Inbox, ChevronRight } from 'lucide-react';
import dashboardService from '../services/dashboardService';
import { useFacility } from '../context/FacilityContext';
import { formatDate as tzFormatDate } from '../utils/datetime';

/**
 * MetricDrillDown — reusable KPI drill-down modal.
 *
 * Opened by a clickable KPI card on the Dashboard or Rentals page. Loads the
 * constituent rows behind the metric from
 *   GET /api/facilities/{facilityId}/dashboard/breakdown?metric=...
 * and lists them with a total that matches the headline number (the backend
 * derives both from the same query, so they can't diverge).
 *
 * Props:
 *   facilityId : string   (required — the facility to scope to)
 *   metric     : string   (revenue | occupancy | past_due | leads |
 *                          monthly_revenue | active_rentals | move_ins |
 *                          move_outs | website_rentals | recurring_billing)
 *   month      : string?  (YYYY-MM, revenue only — a revenue-history bar
 *                          click drills the CLICKED month; omitted = the
 *                          current month, exactly as before)
 *   onClose    : () => void
 *
 * Each row links to its underlying record (payment / rental / customer /
 * lead / unit) via the existing app routes; clicking a row closes the modal
 * and navigates.
 */

const fmtMoney = (n) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 2,
  }).format(Number(n || 0));

// Shared tz-aware formatter: slicing a stored naive-UTC timestamp to its
// UTC date read a day ahead every evening (the Claire Allen move-out
// class, 2026-08-01); bare dates still never shift.
const fmtDateTz = (iso, tz) => {
  if (!iso) return '—';
  return tzFormatDate(iso, tz) || '—';
};

const statusPill = (status) => {
  if (!status) return null;
  const s = String(status).toLowerCase();
  const cls = s === 'occupied' || s === 'rented' || s === 'active' || s === 'converted'
    ? 'bg-emerald-50 text-emerald-600'
    : s === 'delinquent' || s === 'overdue' || s === 'lost'
      ? 'bg-red-50 text-red-500'
      : s === 'available'
        ? 'bg-blue-50 text-blue-600'
        : 'bg-slate-100 text-slate-500';
  return (
    <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${cls}`}>
      {String(status)}
    </span>
  );
};

const Row = ({ row, onNavigate }) => {
  const { currentFacility } = useFacility();
  const clickable = !!row.link;
  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={() => clickable && onNavigate(row.link)}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left border-b border-slate-50 last:border-b-0 transition-colors ${
        clickable ? 'hover:bg-slate-50 cursor-pointer' : 'cursor-default'
      }`}
    >
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-slate-800 truncate">
          {row.label}
        </p>
        {row.sublabel ? (
          <p className="text-[12px] text-slate-400 truncate">{row.sublabel}</p>
        ) : null}
      </div>
      {row.date ? (
        <span className="text-[12px] text-slate-400 shrink-0 hidden sm:block">
          {fmtDateTz(row.date, currentFacility?.timezone)}
        </span>
      ) : null}
      {row.status ? <span className="shrink-0">{statusPill(row.status)}</span> : null}
      {row.amount != null ? (
        <span className="text-[13px] font-semibold text-slate-700 shrink-0 tabular-nums">
          {fmtMoney(row.amount)}
        </span>
      ) : null}
      {clickable ? (
        <ChevronRight className="h-3.5 w-3.5 text-slate-300 shrink-0" />
      ) : null}
    </button>
  );
};

const EmptyState = ({ label }) => (
  <div className="flex flex-col items-center justify-center py-14 text-center">
    <Inbox className="h-6 w-6 text-slate-300 mb-2" />
    <p className="text-[13px] text-slate-400">{label || 'Nothing to show yet.'}</p>
  </div>
);

const MetricDrillDown = ({ facilityId, metric, month, onClose }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!facilityId || !metric) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    dashboardService
      .getBreakdown(facilityId, metric, month ? { month } : {})
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.detail || 'Could not load breakdown.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [facilityId, metric, month]);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleNavigate = useCallback((to) => {
    onClose();
    navigate(to);
  }, [navigate, onClose]);

  const total = data?.total;
  const totalKind = data?.total_kind;
  const totalDisplay =
    totalKind === 'money' ? fmtMoney(total)
      : total != null ? String(total) : '';

  // Flatten groups (occupancy) vs flat rows, and compute whether empty.
  const groups = data?.groups || null;
  const rows = data?.rows || [];
  const isEmpty = !loading && !error && (
    groups ? groups.every((g) => (g.rows || []).length === 0) : rows.length === 0
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={data?.title || 'Metric breakdown'}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between shrink-0">
          <h2 className="text-[14px] font-semibold text-slate-900">
            {data?.title || 'Breakdown'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          )}

          {error && !loading && (
            <div className="m-4 text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {!loading && !error && isEmpty && (
            <EmptyState label={`No ${(data?.title || 'records').toLowerCase()} yet.`} />
          )}

          {!loading && !error && !isEmpty && groups && (
            <>
              {groups.map((g) => (
                <div key={g.key}>
                  <div className="sticky top-0 bg-slate-50/95 backdrop-blur px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                    {g.label} · {g.count}
                  </div>
                  {(g.rows || []).length === 0 ? (
                    <p className="px-4 py-3 text-[12px] text-slate-400">None</p>
                  ) : (
                    g.rows.map((row) => (
                      <Row key={row.id} row={row} onNavigate={handleNavigate} />
                    ))
                  )}
                </div>
              ))}
            </>
          )}

          {!loading && !error && !isEmpty && !groups && (
            rows.map((row) => (
              <Row key={row.id} row={row} onNavigate={handleNavigate} />
            ))
          )}
        </div>

        {/* Footer — total matches the headline KPI */}
        {!loading && !error && (
          <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/40 shrink-0">
            <span className="text-[12px] font-medium text-slate-500 uppercase tracking-wider">
              {data?.total_label || 'Total'}
            </span>
            <span className="text-[15px] font-semibold text-slate-900 tabular-nums">
              {totalDisplay}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default MetricDrillDown;
